import _              from 'underscore';
import React, { FC }  from 'react';
import * as Yup       from "yup";
import { Cell, Grid } from "styled-css-grid";

import { Uuid }                                                                                                                  from "@src/types";
import { useSemanticTools }                                                                                                      from "@src/hooks";
import { formatOption }                                                                                                          from "@src/util";
import { ENTITY_ICONS }                                                                                                          from "@src/constants/ProductEnums";
import { HireTypeDropdown, TextField }                                                                                           from "@src/views/components/forms";
import { BulkUpdateFormControlContainer }                                                                                        from "@src/views/pages/projects/activities/bulk/BulkUpdateFormControlContainer";
import { ProjectActivityBulkUpdateShell, ProjectActivityBulkUpdateShellFormContainerProps, ProjectActivityBulkUpdateShellProps } from "@src/views/pages/projects/activities/bulk/ProjectActivityBulkUpdateShell";
import { ProjectActivityBulkUpdateMultiDialogProps }                                                                             from "@src/views/pages/projects/operations/ProjectActivityBulkUpdateMultiDialog";
import { SELECTFIELD_NONE_VALUE }                                                                                                from "@src/constants";
import { ProjectVersionHireTypeDropdown }                                                                                        from '@src/views/components/forms/dropdowns/ProjectVersionHireTypeDropdown';


const NoResourcesValue = SELECTFIELD_NONE_VALUE,
      NoResourcesLabel = 'None';

type FormTypeAttributes = {
    hireTypeId: Uuid | null,
    headcount: number | null
}

const formDefaultValues = {
    headcount:  null,
    hireTypeId: null,
}

const validationSchema = (
    Yup.object().shape( {
        hireTypeId: Yup.string().nullable().required( 'Please choose an option' ),
        headcount:  Yup.number().when( 'hireTypeId', {
            is:        hireTypeId => ( hireTypeId === NoResourcesValue ),
            then:      Yup.number().nullable(),
            otherwise: Yup.number().min( 0, 'Must be zero or more.' ).required( 'Must be zero or more.' )
        } ),
    } )
)

const mutationAttributeFormatter: ProjectActivityBulkUpdateShellProps<FormTypeAttributes>['prepareFormValuesForMutation'] = ( values ) => {

    const useNoResources = ( values.hireTypeId === NoResourcesValue ) || ( values.headcount == 0 ),
          requiredHires  = [ values ];

    return {
        requiredHireTypes: useNoResources ? [] : requiredHires
    }
}

interface StaticProjectActivityBulkUpdateHireTypeProps extends Pick<ProjectActivityBulkUpdateShellProps<FormTypeAttributes>, 'supportModals' | 'supportToasts'>,
    Pick<ProjectActivityBulkUpdateMultiDialogProps, 'projectId' | 'projectVersionId' | 'projectActivityIds'> {
    formContainer: FC<ProjectActivityBulkUpdateShellFormContainerProps>
}

const StaticProjectActivityBulkUpdateHireTypeDefaultProps = {
    supportModals: false,
    supportToasts: true,
} satisfies Partial<StaticProjectActivityBulkUpdateHireTypeProps>

function StaticProjectActivityBulkUpdateHireType( _props: StaticProjectActivityBulkUpdateHireTypeProps ){

    const props = { ...StaticProjectActivityBulkUpdateHireTypeDefaultProps, ..._props };

    const [ semanticTools ] = useSemanticTools();

    return (

        <ProjectActivityBulkUpdateShell<FormTypeAttributes>
            projectId={ props.projectId }
            projectVersionId={ props.projectVersionId }
            ids={ props.projectActivityIds }
            supportToasts={ props.supportToasts }
            supportModals={ props.supportModals }
            defaultFormValues={ formDefaultValues }
            validationSchema={ validationSchema }
            formContainer={ props.formContainer }
            prepareFormValuesForMutation={ mutationAttributeFormatter }>

            {/* FORM CONTENT */ }
            { ( { formik, isProcessing, isLoading } ) => (

                <BulkUpdateFormControlContainer
                    icon={ ENTITY_ICONS.RESOURCING.HIRES.ENTITY }
                    title={ 'Update Hire Type' }
                    description={ `Migrate the hire type for multiple activities to the one specified. Activities with more than one hire type assigned will be overwritten to the selected single hire type below. Choosing no hire type will not assign a resource.` }>

                    <Grid columns={ `26% 1fr` }>

                        <Cell>

                            <TextField
                                label={ 'Headcount' }
                                descriptionText={ 'Resources needed' }
                                type='number'
                                min={ 1 }
                                name={ `headcount` }
                                placeholder='#'
                                disabled={ isLoading || isProcessing || [ NoResourcesValue ].includes( formik.values.hireTypeId ) }
                            />

                        </Cell>
                        <Cell>

                            <ProjectVersionHireTypeDropdown
                                projectId={ props.projectId }
                                projectVersionId={ props.projectVersionId }
                                fluid
                                disabled={ isLoading || isProcessing }
                                label={ 'Hire Type' }
                                descriptionText={ 'The hire type to reassign the selected activities to' }
                                name={ `hireTypeId` }
                                placeholder={ 'Reassign to...' }
                                multiple={ false }
                                search={ true }
                                selectOnNavigation={ true }
                                prepend={ _.compact( [
                                    formatOption( NoResourcesValue, NoResourcesLabel, { group: ' ' } )
                                ] ) }
                                renderLabel={ semanticTools.blockDropdownRenderLabeling }
                            />

                        </Cell>

                    </Grid>

                </BulkUpdateFormControlContainer>

            ) }

        </ProjectActivityBulkUpdateShell>

    );
}

export {
    StaticProjectActivityBulkUpdateHireType,
    StaticProjectActivityBulkUpdateHireTypeDefaultProps,
    type StaticProjectActivityBulkUpdateHireTypeProps,
};
